/**
 * Definitions for the numbers a rep can reasonably misread.
 *
 * Deliberately NOT one tooltip per metric. A tip on a number that explains
 * itself ("Purchases", "Used units") is noise, and noise teaches people to stop
 * hovering — which costs us the tips that matter. Every entry here earns its
 * place by answering one of four questions the value alone cannot:
 *
 *   1. WHAT WINDOW is this? (the buy profile's rolling 90 ties to no other panel)
 *   2. WHAT POPULATION? (Manheim-only vs not, MMI vs all, CSE vs every org)
 *   3. IS IT MEASURED OR CLAIMED? (dealer-reported and forecast figures)
 *   4. IS IT COMPARABLE to the number sitting next to it? (partial months)
 *
 * Copy rules: one or two sentences, no jargon the rep would not use, and state
 * the trap rather than the mechanism.
 */
export const METRIC_TIPS = {
  // --- core car: Salesforce's numbers, shown as Salesforce's ---
  coreCarRollup:
    "Salesforce's own rollup. It will not match SIFT's sell and buy numbers — different grain, different system of record. Expected, not a defect.",
  coreCarSelfReported:
    'Dealer-reported to Salesforce, not measured. Treat it as what they told us, not what they did.',
  coreCarCurrentMonth:
    "Salesforce's count for the CURRENT month, so it is partial until the month closes. Low early in the month means the month is young, not that the dealer stopped.",

  // --- buy profile: a rolling 90 days that ties to no window on this screen ---
  buyProfileWindow:
    'A rolling 90 days from the buyer feed, which matches no window elsewhere in this drawer — so it will not equal the selected period. It also includes non-Manheim purchases.',
  buyProfileVsMmr:
    'Dollars above or below base MMR, not a percentage. Negative means they buy below market; the national median is about −$149.',
  buyProfileBids:
    'Total bids divided by cars won. High means they chase a lot to land a little.',
  buyProfileDigital:
    'Share of their purchases bought on the Digital channel, out of ALL buys — not Digital versus Simulcast.',
  buyPeriodPricing:
    'Rolling 3-month period, Manheim only. Deliberately a different population from the 90-day profile above, so the two will disagree.',
  buyToMmr:
    'Price paid as a share of MMR. Over 115% renders red — the same line the Manheim Dealer Performance sheet draws.',

  // --- Cox Automotive performance: monthly, mid-month, multi-product ---
  coxAgedInventory:
    "Units in inventory over 45 days, from vAuto. Not the same as SIFT's aged Manheim listings, which count wholesale listings on a different clock.",
  coxPctAged:
    'Aged units as a share of used inventory. Share, not count — a 200-car dealer with 10 aged units is healthier than a 12-car dealer with 10.',
  coxAgedAdvertising:
    'Autotrader VINs advertised 45 days or more. Advertising, not inventory — a car can be aged on one and fresh on the other.',
  coxPctAgedAdvertising:
    "Uses the report's own denominator, so this ties to the Autotrader figure the dealer sees rather than to the VIN count beside it.",
  coxLevel:
    'A level, not a running total. Compare it straight across to last month.',
  coxPace:
    'Month to date against the whole of last month, so the raw numbers are not like for like. The tag projects the partial month forward before comparing.',
  coxPaceEarly:
    'Too early in the month to project a pace, so the raw month-to-date is shown. It will read low against a full prior month.',
  coxIcoPriorOnly:
    'Last month only. The Instant Cash Offer feed does not land within the month, so there is no current-month figure to show.',
  coxFlooredAvgPrice:
    'Last month only — an in-month average would be taken over a part-finished month.',
  coxPredicted:
    "Cox's forecast of what this rooftop should sell. A forecast, not a measurement, and a different number from the dealer-reported monthly wholesale volume.",
  coxRooftopUnattributed:
    'A rooftop figure. It does not line up with any single product feed, so it is not filed under one.',
  coxTradeIns:
    'Trade-ins taken on a retail deal. Not attributable to one product — 44% of rooftops reporting trade-ins carry no VinSolutions flag.',
  coxProductChips:
    'Products on record for this rooftop. A faded chip means not reported, never a confirmed no — the source has no way to say "no".',

  // --- ancillary revenue: the dollars behind the enrolment chips ---
  ancillaryRevenueWindows:
    'The 4-week average covers weeks -5 to -2 and deliberately excludes last week, so last week is measured against a baseline it is not part of. That is why it will not tie exactly to Plan My Day, whose 4-week average includes it.',
  ancillaryRevenueSpread:
    'Breadth of the relationship. Six services billed across twelve of thirteen weeks is a very different attachment from one service in one big week, even when the dollars match.',

  // --- pipeline: the rep's own commitments, not the client's behaviour ---
  pipelineOffTrackOpps:
    'Distinct opportunities tripping at least one off-track signal. Not the sum of the signals below — one deal can trip several and is only counted once here.',
  pipelinePendingWon:
    'Sitting in Pending Won with a close date 30 days to 6 months old. The heaviest signal: the deal was called won and then nothing happened.',
  pipelineStale:
    'No stage change in 60 days or more. Delivery and onboarding stages are excluded, because sitting still is normal there.',
  pipelinePushed:
    'The close date has been moved three or more times and the deal is still due within 90 days. Slipping, not yet stalled.',
  pipelinePastClose:
    'The close date has simply passed and the deal is still open.',
  pipelineWorstScore:
    "The worst single signal on this client, on Plan My Day's 100 / 75 / 50 scale. Rank on this rather than a total, so a handful of minor slips cannot outrank one dead deal.",
  pipelineDeliveryRisk:
    'Deals already marked won that are under 80% of their target and not yet tracked complete. A promise made and not yet delivered, which is why it carries weight.',
  pipelineDeliveryRiskPct:
    'How far behind the worst at-risk won deal is, against its own target. Blank means nothing is at risk — a missing target never counts as off track.',
  pipelineOppActivity:
    'The last time anyone touched the DEAL. Different from the last contact with the dealer, and the two will disagree.',
  pipelineExpectedLift:
    'Extra volume the open deals are expected to bring in if they land. The size of the live pipeline, not a forecast of what will.',

  // --- ancillary ---
  ancillaryReadyShipments:
    'Actual Ready Logistics shipments. Read these rather than the account flag, which is registration and true for almost everyone.',

  // --- engagement: whose contact was it? ---
  engagementCseOnly:
    'CSE-logged contacts only. The all-org total is a different number — the ManEx Trade Desk alone is about 30% of everything logged.',
  engagementByRole:
    'Who logged the contact. Worth separating: a ManEx or Ready call is not CSE engagement.',
  engagementLiveFloor:
    'Contacts where someone actually reached the dealer. A floor, not a total — about 18% of tasks carry no channel marker.',
  engagementInPerson:
    'In-person visits in the last 90 days, any org.',
  engagementCseLive:
    'Last CSE contact where someone actually reached the dealer: in person, a live phone call or a virtual meeting. Left messages and emails do not count. A floor — about 18% of tasks carry no channel marker.',
  engagementCseVisit:
    'Last CSE in-person visit. Broken appointments and vacant addresses do not count. The score weighs a live conversation most, then a visit, then any contact attempt.',

  // --- efficiency rates: what is divided by what ---
  effectiveness:
    "Sales ÷ listings. Plan My Week's Effectiveness; SIFT calls it sell-through. A car listed three times counts as three listings.",
  offeringConversion:
    'Sell cycles that ended in a sale ÷ sell cycles started. One cycle can span several listings. No Plan My Week counterpart.',
  offsiteConversion:
    'Sales from inspected cars ÷ inspections. Uncapped, like Plan My Week.',
  liveConversion:
    'Net live-lane sales ÷ live-lane listings. This is the InLane efficiency the health score reads.',
  timedConversion:
    'Net timed-sale sales ÷ timed-sale listings.',

  // --- what "market" and "peers" mean ---
  marketDefinition:
    "Market = every client in this client's territory market center. If the market itself is down on last year, a decline is not all this client's, so the health score reduces it.",
  peerDefinition:
    'Peers = the other clients in the same CSE territory. The percentile is where this client ranks among them.',

  // --- MMR retention: what reps call % of MMR ---
  mmrRetentionSell:
    'MMR retention: sold price ÷ MMR at sale, volume-weighted. 100% = sold at MMR. Blank when too few cars carry an MMR to quote it.',
  mmrRetentionBuy:
    'MMR retention on what was bought: price paid ÷ MMR, volume-weighted. Lower means cars went cheaper against market there — a better place to buy.',
} as const;

export type MetricTipKey = keyof typeof METRIC_TIPS;
